import { useEffect, useRef, useState } from "react";
import { Button, Card, Dropdown, Empty, Spin, Switch, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  DollarOutlined,
  DownOutlined,
  EditOutlined,
  MoreOutlined,
  RightOutlined,
} from "@ant-design/icons";
import PrismTruncatedText from "./PrismTruncatedText";
import {
  canManageAnyPrismOffice,
  canManagePrismRecord,
  CRITICALITY_COLORS,
  CRITICALITY_LABELS,
  formatDate,
  formatLabel,
  isLimitedPrismRecord,
  STATUS_COLORS,
} from "./prismSubscriptionUtils";

const renderStatus = (status) => {
  if (!status) return "-";

  return (
    <Tag color={STATUS_COLORS[status] || "blue"}>{formatLabel(status)}</Tag>
  );
};

const renderCriticality = (criticality) => {
  if (!criticality) return "-";

  return (
    <Tag
      color={CRITICALITY_COLORS[criticality] || "default"}
      style={
        criticality === "overdue"
          ? { color: "#fff", borderColor: "#000" }
          : undefined
      }
    >
      {CRITICALITY_LABELS[criticality] || formatLabel(criticality)}
    </Tag>
  );
};

const PrismSubscriptionCardView = ({
  groups = [],
  loading = false,
  details = {},
  deleteLoading = false,
  activationLoadingId = null,
  focusedSubscriptionId = null,
  onAddPayment,
  onEdit,
  onDelete,
  onToggle,
  onView,
  onFocusHandled,
}) => {
  const [expandedOfficeKeys, setExpandedOfficeKeys] = useState([]);
  const [collapsedSubDepartmentKeys, setCollapsedSubDepartmentKeys] = useState(
    [],
  );
  const handledFocusIdRef = useRef(null);

  useEffect(() => {
    if (
      !focusedSubscriptionId ||
      String(handledFocusIdRef.current) === String(focusedSubscriptionId)
    ) {
      return undefined;
    }

    let targetOffice = null;
    let targetSubDepartment = null;

    groups.some((officeGroup) => {
      const officeRecord = officeGroup.records?.find(
        (record) => String(record.id) === String(focusedSubscriptionId),
      );

      if (officeRecord) {
        targetOffice = officeGroup;
        return true;
      }

      const subDepartment = officeGroup.subDepartments?.find((group) =>
        group.records.some(
          (record) => String(record.id) === String(focusedSubscriptionId),
        ),
      );

      if (!subDepartment) return false;

      targetOffice = officeGroup;
      targetSubDepartment = subDepartment;
      return true;
    });

    if (!targetOffice) return undefined;

    handledFocusIdRef.current = focusedSubscriptionId;
    setExpandedOfficeKeys((currentKeys) =>
      currentKeys.includes(targetOffice.key)
        ? currentKeys
        : [...currentKeys, targetOffice.key],
    );

    if (targetSubDepartment) {
      setCollapsedSubDepartmentKeys((currentKeys) =>
        currentKeys.filter((key) => key !== targetSubDepartment.key),
      );
    }

    const scrollTimer = window.setTimeout(() => {
      const focusedCard = Array.from(
        document.querySelectorAll("[data-subscription-id]"),
      ).find(
        (element) =>
          String(element.dataset.subscriptionId) ===
          String(focusedSubscriptionId),
      );

      focusedCard?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      onFocusHandled?.();
    }, 0);

    return () => window.clearTimeout(scrollTimer);
  }, [focusedSubscriptionId, groups, onFocusHandled]);

  const toggleKey = (setter, key) => {
    setter((currentKeys) =>
      currentKeys.includes(key)
        ? currentKeys.filter((currentKey) => currentKey !== key)
        : [...currentKeys, key],
    );
  };

  const isPartialAdmin = canManageAnyPrismOffice(details);

  const renderCards = (subscriptions) => (
    <div className="prism-subscription-card-grid">
      {subscriptions.map((subscription, index) => {
        const canManageRecord = canManagePrismRecord(details, subscription);
        const isLimitedRecord = isLimitedPrismRecord(subscription, details);
        const showRestrictedLabel = isLimitedRecord && isPartialAdmin;
        const subscriptionLabel =
          subscription.subscription_name || `Subscription ${index + 1}`;
        const actionItems = [
          {
            key: "payment",
            icon: <DollarOutlined />,
            label: "Add Payment",
          },
          {
            key: "edit",
            icon: <EditOutlined />,
            label: "Edit Subscription",
          },
          {
            key: "delete",
            danger: true,
            disabled: deleteLoading,
            icon: <DeleteOutlined />,
            label: "Delete Subscription",
          },
        ];
        const handleAction = ({ key, domEvent }) => {
          domEvent?.preventDefault();
          domEvent?.stopPropagation();

          if (key === "payment") onAddPayment?.(subscription);
          if (key === "edit") onEdit?.(subscription);
          if (key === "delete") onDelete?.(subscription);
        };

        return (
          <Card
            className={`prism-subscription-card${
              String(subscription.id) === String(focusedSubscriptionId)
                ? " prism-subscription-card--focused"
                : ""
            }`}
            data-subscription-id={subscription.id}
            key={subscription.id ?? subscription.key ?? index}
            onClick={(event) => {
              if (event.target.closest(".prism-subscription-card__controls")) {
                return;
              }

              onView?.(subscription);
            }}
            onKeyDown={(event) => {
              if (
                event.target === event.currentTarget &&
                (event.key === "Enter" || event.key === " ")
              ) {
                event.preventDefault();
                onView?.(subscription);
              }
            }}
            role="button"
            size="small"
            tabIndex={0}
          >
            <div className="prism-subscription-card__header">
              <div className="prism-subscription-card__title">
                <PrismTruncatedText value={subscriptionLabel} lines={2} />
              </div>
              {canManageRecord && (
                <div className="prism-subscription-card__controls">
                  <Tooltip
                    title={
                      subscription.is_enabled !== false
                        ? "Disable this subscription"
                        : "Enable this subscription"
                    }
                  >
                    <Switch
                      aria-label={`${
                        subscription.is_enabled !== false
                          ? "Disable"
                          : "Enable"
                      } ${subscriptionLabel}`}
                      checked={subscription.is_enabled !== false}
                      loading={activationLoadingId === subscription.id}
                      onClick={(checked, event) => {
                        event.stopPropagation();
                        onToggle?.(subscription, checked);
                      }}
                      size="small"
                    />
                  </Tooltip>
                  <Dropdown
                    menu={{ items: actionItems, onClick: handleAction }}
                    placement="bottomRight"
                    trigger={["click"]}
                  >
                    <Button
                      aria-label={`Actions for ${subscriptionLabel}`}
                      className="prism-subscription-card__menu-button"
                      icon={<MoreOutlined />}
                      loading={deleteLoading}
                      onClick={(event) => event.stopPropagation()}
                      size="small"
                      type="text"
                    />
                  </Dropdown>
                </div>
              )}
            </div>

            <div className="prism-subscription-card__description">
              <PrismTruncatedText
                value={subscription.subscription_details}
                lines={2}
              />
            </div>

            {isPartialAdmin &&
              (showRestrictedLabel ? (
                <Tooltip title="Status, criticality, and renewal details are restricted to the assigned office or unit.">
                  <div className="prism-subscription-card__summary">
                    <span className="prism-subscription-card__restricted">
                      Restricted
                    </span>
                  </div>
                </Tooltip>
              ) : (
                <div className="prism-subscription-card__summary">
                  <div className="prism-subscription-card__renewal">
                    <span className="prism-subscription-card__meta-label">
                      Renewal
                    </span>
                    <span className="prism-subscription-card__meta-value">
                      {formatDate(subscription.contract_renewal_date)}
                    </span>
                  </div>
                  <div className="prism-subscription-card__tags">
                    {subscription.is_enabled === false ? (
                      <Tag color="default">Disabled</Tag>
                    ) : (
                      <>
                        {renderStatus(subscription.contract_status)}
                        {renderCriticality(subscription.criticality)}
                      </>
                    )}
                  </div>
                </div>
              ))}
          </Card>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <div className="prism-subscription-card-view__loading">
        <Spin tip="Loading subscriptions..." />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="prism-subscription-card-view__empty">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No subscriptions found"
        />
      </div>
    );
  }

  return (
    <div className="prism-subscription-card-view">
      {groups.map((officeGroup) => {
        const isOfficeCollapsed = !expandedOfficeKeys.includes(officeGroup.key);

        return (
          <section className="prism-subscription-office-group" key={officeGroup.key}>
            <Button
              aria-expanded={!isOfficeCollapsed}
              aria-label={`${isOfficeCollapsed ? "Expand" : "Collapse"} ${officeGroup.label}`}
              block
              className="prism-subscription-office-group__toggle"
              icon={isOfficeCollapsed ? <RightOutlined /> : <DownOutlined />}
              onClick={() =>
                toggleKey(setExpandedOfficeKeys, officeGroup.key)
              }
              type="text"
            >
              <span className="prism-subscription-office-group__label">
                {officeGroup.label}
              </span>
              <span className="prism-subscription-office-group__count">
                {officeGroup.count} subscription{officeGroup.count === 1 ? "" : "s"}
              </span>
            </Button>

            {!isOfficeCollapsed && (
              <div className="prism-subscription-office-group__content">
                {officeGroup.hasSubDepartments
                  ? officeGroup.subDepartments.map((subDepartmentGroup) => {
                      const isSubDepartmentCollapsed =
                        collapsedSubDepartmentKeys.includes(
                          subDepartmentGroup.key,
                        );

                      return (
                        <section
                          className="prism-subscription-sub-department-group"
                          key={subDepartmentGroup.key}
                        >
                          <Button
                            aria-expanded={!isSubDepartmentCollapsed}
                            aria-label={`${isSubDepartmentCollapsed ? "Expand" : "Collapse"} ${subDepartmentGroup.label}`}
                            block
                            className="prism-subscription-sub-department-group__toggle"
                            icon={
                              isSubDepartmentCollapsed ? (
                                <RightOutlined />
                              ) : (
                                <DownOutlined />
                              )
                            }
                            onClick={() =>
                              toggleKey(
                                setCollapsedSubDepartmentKeys,
                                subDepartmentGroup.key,
                              )
                            }
                            type="text"
                          >
                            <span className="prism-subscription-sub-department-group__label">
                              {subDepartmentGroup.label}
                            </span>
                            <span className="prism-subscription-sub-department-group__count">
                              {subDepartmentGroup.records.length}
                            </span>
                          </Button>
                          {!isSubDepartmentCollapsed &&
                            renderCards(subDepartmentGroup.records)}
                        </section>
                      );
                    })
                  : renderCards(officeGroup.records)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default PrismSubscriptionCardView;
